import React, { useEffect, useState } from "react";
import {
  Phone,
  MapPin,
  Instagram,
  Facebook,
  ArrowUpRight,
  Leaf,
  Sparkles,
  Activity,
  ShieldCheck,
  HeartPulse,
  Star,
  Quote,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import { LOGO, LOGO_LIGHT, IMG, THERAPIES, PILLARS, PROGRAMS, TESTIMONIALS } from "@/data/ojasvaya";

const PILLAR_ICONS = [HeartPulse, Leaf, Activity, Sparkles, ShieldCheck];

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Therapies", href: "#therapies" },
  { label: "Programs", href: "#programs" },
  { label: "About", href: "#about" },
  { label: "Visit Us", href: "#visit" },
];

/* ========== BRAND INTRO ========== */
const BrandIntro = ({ onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3700);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="oj-intro" data-testid="brand-intro">
      <div
        className="oj-intro-bg-orb"
        style={{ width: 560, height: 560, background: "#B8893E", top: "-10%", left: "-10%" }}
      />
      <div
        className="oj-intro-bg-orb"
        style={{ width: 500, height: 500, background: "#2C6B45", bottom: "-15%", right: "-5%" }}
      />
      <div className="oj-intro-center">
        <img
          src={LOGO_LIGHT}
          alt="Ojasvaya — Integrated Health & Wellness"
          className="oj-intro-logo-mark"
        />
        <div className="oj-intro-divider" />
        <div className="oj-intro-tagline">Restore · Revitalize · Renew</div>
        <div className="oj-intro-sub">Where science meets holistic healing</div>
      </div>
    </div>
  );
};

/* ========== HEADER ========== */
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--oj-cream)]/95 backdrop-blur-md shadow-[0_1px_0_rgba(184,137,62,0.2)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <a href="#home" className="flex items-center" data-testid="header-logo">
          <img
            src={scrolled ? LOGO : LOGO_LIGHT}
            alt="Ojasvaya — Integrated Health & Wellness"
            className={`w-auto object-contain transition-all duration-300 ${
              scrolled ? "h-16 lg:h-20" : "h-24 lg:h-28"
            }`}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`oj-link text-[13px] tracking-[0.18em] uppercase font-medium ${
                scrolled ? "text-[var(--oj-charcoal)]" : "text-[var(--oj-ivory)]"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#visit"
            data-testid="header-book-btn"
            className="oj-btn oj-btn-gold hidden md:inline-flex"
          >
            Book a Visit <ArrowUpRight size={16} />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-[var(--oj-emerald)]" : "text-[var(--oj-ivory)]"}`}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[var(--oj-cream)] border-t border-[var(--oj-gold)]/20 py-6 px-6 space-y-4">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              data-testid={`mnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block font-display text-sm tracking-[0.2em] text-[var(--oj-emerald)] uppercase py-2"
            >
              {n.label}
            </a>
          ))}
          <a href="#visit" onClick={() => setOpen(false)} className="oj-btn oj-btn-gold w-full mt-2" data-testid="mnav-book">
            Book a Visit
          </a>
        </div>
      )}
    </header>
  );
};

/* ========== HERO ========== */
const Hero = () => (
  <section id="home" className="relative min-h-[100vh] flex items-end overflow-hidden" data-testid="hero-section">
    <img
      src={IMG.lobby}
      alt="Ojasvaya reception"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 oj-hero-veil" />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--oj-emerald-deep)]/95 via-transparent to-[var(--oj-emerald-deep)]/30" />

    <div className="relative max-w-[1400px] mx-auto w-full px-6 lg:px-12 pb-24 lg:pb-32 pt-32">
      <div className="oj-rise max-w-3xl">
        <span className="oj-divider mb-8 text-[var(--oj-gold-light)]">
          <span style={{ color: "var(--oj-gold-light)" }}>Restore · Revitalize · Renew</span>
        </span>
        <h1 className="font-serif text-[var(--oj-ivory)] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] font-light tracking-tight oj-hero-text-shadow">
          Where <em className="italic text-[var(--oj-gold-light)] font-normal">science</em> meets
          <br />
          holistic <em className="italic text-[var(--oj-gold-light)] font-normal">healing</em>.
        </h1>
        <p className="mt-8 text-[var(--oj-ivory)] text-lg max-w-xl leading-relaxed oj-hero-text-shadow">
          Mumbai's integrated wellness sanctuary — where ancient Ayurveda and the world's most advanced
          medical therapies converge under one roof, designed around you.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#visit" className="oj-btn oj-btn-gold" data-testid="hero-cta-book">
            Book a Consultation <ArrowUpRight size={16} />
          </a>
          <a href="#therapies" className="oj-btn oj-btn-ghost" data-testid="hero-cta-therapies">
            Explore Therapies
          </a>
        </div>

        <div className="mt-14 flex items-center gap-8 text-[var(--oj-ivory)]/70 text-xs tracking-[0.2em] uppercase">
          <div className="flex items-center gap-2">
            <Star size={14} className="text-[var(--oj-gold-light)]" />
            <span>18+ Therapies</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Star size={14} className="text-[var(--oj-gold-light)]" />
            <span>Doctor-Led Protocols</span>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Star size={14} className="text-[var(--oj-gold-light)]" />
            <span>Mumbai · Bhandup (W)</span>
          </div>
        </div>
      </div>
    </div>

    {/* Marquee strip */}
    <div className="absolute bottom-0 inset-x-0 border-t border-[var(--oj-gold)]/30 bg-[var(--oj-emerald-deep)]/85 backdrop-blur-sm py-3 overflow-hidden">
      <div className="oj-marquee flex gap-12 whitespace-nowrap text-[var(--oj-gold-light)] font-display text-xs tracking-[0.32em] uppercase">
        {Array(2)
          .fill(0)
          .map((_, i) => (
            <React.Fragment key={i}>
              {["Hyperbaric Oxygen", "Cryotherapy", "Panchakarma", "Ozone Therapy", "EBOO", "Red Light", "Stem Cell", "Peptide", "PRP"].map((t) => (
                <span key={t + i} className="flex items-center gap-12">
                  {t} <span className="text-[var(--oj-gold)]">✦</span>
                </span>
              ))}
            </React.Fragment>
          ))}
      </div>
    </div>
  </section>
);

/* ========== PILLARS ========== */
const Pillars = () => (
  <section className="bg-[var(--oj-cream)] py-24 lg:py-32 oj-paper" data-testid="pillars-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="text-center mb-16">
        <span className="oj-divider">Our Five Pillars</span>
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--oj-emerald)] mt-5 font-light">
          One sanctuary. <em className="italic text-[var(--oj-gold)]">Five disciplines.</em>
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 lg:gap-6">
        {PILLARS.map((p, i) => {
          const Icon = PILLAR_ICONS[i];
          return (
            <div
              key={p.name}
              data-testid={`pillar-${i}`}
              className="oj-card rounded-sm p-7 text-center bg-white"
            >
              <div className="mx-auto w-14 h-14 rounded-full border border-[var(--oj-gold)]/40 flex items-center justify-center mb-5 bg-[var(--oj-cream)]">
                <Icon size={22} className="text-[var(--oj-emerald)]" />
              </div>
              <h3 className="font-serif text-lg text-[var(--oj-emerald)] leading-tight mb-2">
                {p.name}
              </h3>
              <p className="text-xs text-[var(--oj-stone)] leading-relaxed">{p.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ========== ABOUT / DIFFERENCE ========== */
const About = () => (
  <section id="about" className="bg-[var(--oj-emerald)] text-[var(--oj-ivory)] py-24 lg:py-36 relative overflow-hidden" data-testid="about-section">
    <div className="absolute -top-20 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--oj-gold)]/10 blur-3xl" />
    <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--oj-emerald-soft)]/20 blur-3xl" />

    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-5 order-2 lg:order-1">
        <div className="relative">
          <img
            src={IMG.panchakarma}
            alt="Panchakarma room at Ojasvaya"
            className="w-full aspect-[4/5] object-cover rounded-sm shadow-2xl"
          />
          <div className="absolute -bottom-8 -right-8 w-44 h-44 border border-[var(--oj-gold)] hidden md:block" />
          <div className="absolute -top-6 -left-6 bg-[var(--oj-gold)] text-[var(--oj-emerald-deep)] px-6 py-4 rounded-sm shadow-xl hidden md:block">
            <div className="font-display text-[10px] tracking-[0.3em] uppercase">Est.</div>
            <div className="font-serif text-3xl leading-none">2026</div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-7 order-1 lg:order-2">
        <span className="oj-divider mb-6 text-[var(--oj-gold-light)]">The Ojasvaya Way</span>
        <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[1.05]">
          A clinic for the few who refuse to choose between{" "}
          <em className="italic text-[var(--oj-gold-light)]">tradition</em> and{" "}
          <em className="italic text-[var(--oj-gold-light)]">tomorrow</em>.
        </h2>
        <p className="mt-8 text-[var(--oj-ivory)]/80 text-base lg:text-lg leading-relaxed max-w-2xl">
          Ojasvaya is built on the conviction that real wellness lives at the intersection of disciplines.
          Our physicians blend classical Ayurvedic protocols with hyperbaric oxygen, peptide and regenerative
          medicine — pursuing not just relief, but renewal.
        </p>

        <div className="mt-12 grid sm:grid-cols-3 gap-8">
          {[
            { kicker: "01", title: "Ancient Wisdom", desc: "Authentic Panchakarma & Ayurvedic diagnostics, practiced as taught — not diluted." },
            { kicker: "02", title: "Modern Science", desc: "HBOT, EBOO, Cryo, Stem Cell & Peptide therapies under medical supervision." },
            { kicker: "03", title: "Personal Protocol", desc: "Every guest receives a bespoke protocol — never a package off the shelf." },
          ].map((b) => (
            <div key={b.kicker}>
              <div className="font-display text-[var(--oj-gold-light)] text-xs tracking-[0.32em] mb-3">
                {b.kicker}
              </div>
              <h3 className="font-serif text-2xl mb-2">{b.title}</h3>
              <p className="text-sm text-[var(--oj-ivory)]/70 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ========== SIGNATURE THERAPIES ========== */
const Signature = () => {
  const featured = THERAPIES.filter((t) => t.featured);
  return (
    <section className="bg-[var(--oj-cream)] py-24 lg:py-32" data-testid="signature-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6">
          <div>
            <span className="oj-divider mb-5">Signature Therapies</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[var(--oj-emerald)] font-light leading-tight">
              Six experiences that <em className="italic text-[var(--oj-gold)]">define</em> Ojasvaya.
            </h2>
          </div>
          <a href="#therapies" className="oj-link text-[var(--oj-emerald)] text-sm tracking-[0.2em] uppercase font-medium">
            View all 18 therapies →
          </a>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((t, i) => (
            <a
              key={t.slug}
              href={`#${t.slug}`}
              data-testid={`signature-card-${t.slug}`}
              className="oj-card group rounded-sm block"
            >
              <div className="relative h-72 overflow-hidden">
                <img src={t.img} alt={t.name} className="oj-card-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--oj-emerald-deep)]/85 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-display text-[10px] tracking-[0.32em] text-[var(--oj-gold-light)] uppercase bg-[var(--oj-emerald-deep)]/70 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                  {t.category}
                </span>
                <span className="absolute top-4 right-4 font-serif text-[var(--oj-gold-light)] text-2xl">
                  0{i + 1}
                </span>
              </div>
              <div className="p-7">
                <h3 className="font-serif text-2xl text-[var(--oj-emerald)] mb-2 group-hover:text-[var(--oj-gold-deep)] transition-colors">
                  {t.name}
                </h3>
                <p className="text-sm text-[var(--oj-stone)] leading-relaxed mb-5">{t.short}</p>
                <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--oj-gold-deep)] font-semibold">
                  Learn more <ChevronRight size={14} />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== ALL THERAPIES GRID ========== */
const TherapyGrid = () => {
  const [active, setActive] = useState("All");
  const cats = ["All", "Detox & Cellular", "Regenerative", "Ayurveda", "Aesthetic", "Recovery & Pain"];
  const list = active === "All" ? THERAPIES : THERAPIES.filter((t) => t.category === active);

  return (
    <section id="therapies" className="bg-[var(--oj-ivory)] py-24 lg:py-32 border-y border-[var(--oj-gold)]/15" data-testid="therapies-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <span className="oj-divider">All Therapies</span>
          <h2 className="font-serif text-4xl lg:text-5xl text-[var(--oj-emerald)] mt-5 font-light">
            Eighteen therapies. <em className="italic text-[var(--oj-gold)]">One protocol</em> for you.
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/\s/g, "-").toLowerCase()}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2 rounded-full text-xs tracking-[0.18em] uppercase font-medium transition-all border ${
                active === c
                  ? "bg-[var(--oj-emerald)] text-[var(--oj-ivory)] border-[var(--oj-emerald)]"
                  : "bg-transparent text-[var(--oj-emerald)] border-[var(--oj-emerald)]/25 hover:border-[var(--oj-gold)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {list.map((t) => (
            <a
              key={t.slug}
              href={`#${t.slug}`}
              data-testid={`therapy-${t.slug}`}
              className="oj-card rounded-sm group"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={t.img} alt={t.name} className="oj-card-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[var(--oj-emerald-deep)]/10 group-hover:bg-[var(--oj-emerald-deep)]/30 transition" />
              </div>
              <div className="p-5">
                <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-2">
                  {t.category}
                </div>
                <h3 className="font-serif text-lg text-[var(--oj-emerald)] leading-tight">{t.name}</h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== PROGRAMS ========== */
const Programs = () => (
  <section id="programs" className="bg-[var(--oj-cream)] py-24 lg:py-32" data-testid="programs-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="text-center mb-16 max-w-2xl mx-auto">
        <span className="oj-divider">Curated Journeys</span>
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--oj-emerald)] mt-5 font-light">
          Programmes designed for <em className="italic text-[var(--oj-gold)]">where you are</em> —
          and where you want to go.
        </h2>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        {PROGRAMS.map((p, i) => {
          const isGold = p.color === "gold";
          return (
            <div
              key={p.title}
              data-testid={`program-${i}`}
              className={`relative p-10 rounded-sm overflow-hidden group transition-all duration-500 ${
                isGold
                  ? "bg-[var(--oj-gold)] text-[var(--oj-emerald-deep)]"
                  : "bg-[var(--oj-emerald)] text-[var(--oj-ivory)]"
              }`}
            >
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20 group-hover:opacity-30 transition" style={{
                background: isGold ? "var(--oj-emerald-deep)" : "var(--oj-gold)"
              }} />
              <div className="relative">
                <div className={`font-display text-xs tracking-[0.32em] uppercase mb-4 ${isGold ? "text-[var(--oj-emerald-deep)]/80" : "text-[var(--oj-gold-light)]"}`}>
                  Programme · 0{i + 1}
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl mb-3 leading-tight">{p.title}</h3>
                <div className={`text-xs tracking-[0.2em] uppercase mb-6 ${isGold ? "text-[var(--oj-emerald-deep)]/70" : "text-[var(--oj-ivory)]/70"}`}>
                  {p.duration}
                </div>
                <p className={`text-sm leading-relaxed mb-8 ${isGold ? "text-[var(--oj-emerald-deep)]/90" : "text-[var(--oj-ivory)]/85"}`}>
                  {p.desc}
                </p>
                <a
                  href="#visit"
                  className={`inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold ${
                    isGold ? "text-[var(--oj-emerald-deep)]" : "text-[var(--oj-gold-light)]"
                  }`}
                  data-testid={`program-cta-${i}`}
                >
                  Enquire <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ========== TESTIMONIALS ========== */
const Testimonials = () => (
  <section className="bg-[var(--oj-emerald-deep)] text-[var(--oj-ivory)] py-24 lg:py-32 relative overflow-hidden" data-testid="testimonials-section">
    <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(var(--oj-gold-light) 1px, transparent 1px)",
      backgroundSize: "24px 24px",
    }} />
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
      <div className="text-center mb-16">
        <span className="oj-divider text-[var(--oj-gold-light)]">Voices</span>
        <h2 className="font-serif text-4xl lg:text-5xl mt-5 font-light">
          Trusted by those who <em className="italic text-[var(--oj-gold-light)]">care deeply</em> about how they live.
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={i}
            data-testid={`testimonial-${i}`}
            className="bg-[var(--oj-emerald)]/40 backdrop-blur-sm border border-[var(--oj-gold)]/20 p-8 rounded-sm"
          >
            <Quote className="text-[var(--oj-gold-light)] mb-5" size={28} />
            <blockquote className="font-serif text-xl leading-relaxed text-[var(--oj-ivory)] italic mb-6">
              "{t.quote}"
            </blockquote>
            <figcaption className="border-t border-[var(--oj-gold)]/20 pt-4">
              <div className="font-medium text-[var(--oj-gold-light)]">{t.name}</div>
              <div className="text-xs tracking-[0.2em] uppercase text-[var(--oj-ivory)]/60 mt-1">
                {t.role}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

/* ========== VISIT ========== */
const Visit = () => (
  <section id="visit" className="bg-[var(--oj-cream)] py-24 lg:py-32" data-testid="visit-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <span className="oj-divider mb-6">Visit Us</span>
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--oj-emerald)] font-light leading-tight">
          Step into the <em className="italic text-[var(--oj-gold)]">Ojasvaya</em> sanctuary.
        </h2>
        <p className="mt-6 text-[var(--oj-stone)] leading-relaxed">
          Our flagship centre in Bhandup is open seven days a week. Begin with a complimentary
          15-minute consultation to design your protocol.
        </p>

        <div className="mt-10 space-y-6">
          <div className="flex gap-4" data-testid="visit-address">
            <div className="w-11 h-11 rounded-full bg-[var(--oj-emerald)] flex items-center justify-center flex-shrink-0">
              <MapPin size={18} className="text-[var(--oj-gold-light)]" />
            </div>
            <div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-1">
                Address
              </div>
              <div className="font-serif text-lg text-[var(--oj-emerald)] leading-snug">
                Mez. Floor, Lakhi Industry House,
                <br />
                L.B.S. Marg, Bhandup (W),
                <br />
                Mumbai 400078
              </div>
            </div>
          </div>

          <div className="flex gap-4" data-testid="visit-phone">
            <div className="w-11 h-11 rounded-full bg-[var(--oj-emerald)] flex items-center justify-center flex-shrink-0">
              <Phone size={18} className="text-[var(--oj-gold-light)]" />
            </div>
            <div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-1">
                Call
              </div>
              <a href="tel:+919321005002" className="font-serif text-lg text-[var(--oj-emerald)] hover:text-[var(--oj-gold-deep)]">
                +91 93210 05002
              </a>
            </div>
          </div>

          <div className="flex gap-4" data-testid="visit-whatsapp">
            <div className="w-11 h-11 rounded-full bg-[var(--oj-emerald)] flex items-center justify-center flex-shrink-0">
              <Phone size={18} className="text-[var(--oj-gold-light)]" />
            </div>
            <div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-1">
                WhatsApp
              </div>
              <a href="https://wa.me/919321009006" target="_blank" rel="noreferrer" className="font-serif text-lg text-[var(--oj-emerald)] hover:text-[var(--oj-gold-deep)]">
                +91 93210 09006
              </a>
            </div>
          </div>

          <div className="flex gap-4" data-testid="visit-social">
            <div className="w-11 h-11 rounded-full bg-[var(--oj-emerald)] flex items-center justify-center flex-shrink-0">
              <Instagram size={18} className="text-[var(--oj-gold-light)]" />
            </div>
            <div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-1">
                Follow
              </div>
              <a
                href="https://instagram.com/ojasvaya_official"
                target="_blank"
                rel="noreferrer"
                className="font-serif text-lg text-[var(--oj-emerald)] hover:text-[var(--oj-gold-deep)]"
              >
                @ojasvaya_official
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="https://wa.me/919321009006" target="_blank" rel="noreferrer" className="oj-btn oj-btn-primary" data-testid="visit-whatsapp-btn">
            WhatsApp Us
          </a>
          <a href="tel:+919321005002" className="oj-btn oj-btn-outline-dark" data-testid="visit-call-btn">
            Call Now
          </a>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="rounded-sm overflow-hidden border border-[var(--oj-gold)]/20 shadow-xl bg-white" data-testid="visit-map">
          <iframe
            title="Ojasvaya Map"
            src="https://www.google.com/maps?q=Lakhi+Industry+House+LBS+Marg+Bhandup+West+Mumbai&output=embed"
            width="100%"
            height="520"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="bg-white border border-[var(--oj-gold)]/20 p-5 rounded-sm">
            <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-2">
              Hours
            </div>
            <div className="text-[var(--oj-charcoal)]">
              Mon – Sat · 9:00 AM – 8:00 PM
              <br />
              Sun · By appointment
            </div>
          </div>
          <div className="bg-white border border-[var(--oj-gold)]/20 p-5 rounded-sm">
            <div className="font-display text-[10px] tracking-[0.3em] text-[var(--oj-gold-deep)] uppercase mb-2">
              First Visit
            </div>
            <div className="text-[var(--oj-charcoal)]">
              Complimentary 15-min consult.
              <br />
              Bring any recent reports.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ========== FOOTER ========== */
const Footer = () => (
  <footer className="bg-[var(--oj-emerald-deep)] text-[var(--oj-ivory)] pt-20 pb-8" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid md:grid-cols-12 gap-10 pb-14 border-b border-[var(--oj-gold)]/20">
        <div className="md:col-span-5">
          <div className="mb-5">
            <img src={LOGO_LIGHT} alt="Ojasvaya" className="h-16 w-auto object-contain mb-3" />
            <div className="font-display text-[10px] tracking-[0.32em] text-[var(--oj-gold-light)]">
              Restore · Revitalize · Renew
            </div>
          </div>
          <p className="text-sm text-[var(--oj-ivory)]/70 leading-relaxed max-w-md">
            An integrated health & wellness centre in Mumbai pairing classical Ayurveda with the
            world's most advanced medical therapies.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="font-display text-[10px] tracking-[0.32em] text-[var(--oj-gold-light)] uppercase mb-4">
            Explore
          </div>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.href}>
                <a href={n.href} className="oj-link text-[var(--oj-ivory)]/85">
                  {n.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="font-display text-[10px] tracking-[0.32em] text-[var(--oj-gold-light)] uppercase mb-4">
            Reach Us
          </div>
          <div className="text-sm text-[var(--oj-ivory)]/85 space-y-3">
            <p>Mez. Floor, Lakhi Industry House, L.B.S. Marg, Bhandup (W), Mumbai 400078</p>
            <p>+91 93210 05002 · +91 93210 09006 · hello@ojasvaya.com</p>
            <div className="flex gap-3 mt-4">
              <a href="https://instagram.com/ojasvaya_official" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-[var(--oj-gold)]/40 flex items-center justify-center hover:bg-[var(--oj-gold)] hover:text-[var(--oj-emerald-deep)] transition" data-testid="footer-instagram">
                <Instagram size={15} />
              </a>
              <a href="#" className="w-9 h-9 rounded-full border border-[var(--oj-gold)]/40 flex items-center justify-center hover:bg-[var(--oj-gold)] hover:text-[var(--oj-emerald-deep)] transition" data-testid="footer-facebook">
                <Facebook size={15} />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-[var(--oj-ivory)]/50">
        <div>© {new Date().getFullYear()} Ojasvaya Integrated Health & Wellness Centre. All rights reserved.</div>
        <div className="font-display tracking-[0.32em] uppercase">ojasvaya.com</div>
      </div>
    </div>
  </footer>
);

/* ========== FLOATING WHATSAPP ========== */
const FloatingWhatsApp = () => (
  <a
    href="https://wa.me/919321009006"
    target="_blank"
    rel="noreferrer"
    data-testid="floating-whatsapp"
    aria-label="Chat on WhatsApp"
    className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
    style={{ boxShadow: "0 12px 28px -8px rgba(37, 211, 102, 0.5)" }}
  >
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
);

/* ========== HOME ========== */
const Home = () => {
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    if (!introDone) {
      document.body.style.overflow = "hidden";
      // Match body bg to intro to avoid any flash
      document.body.style.background = "#0F3220";
    } else {
      document.body.style.overflow = "";
      document.body.style.background = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.background = "";
    };
  }, [introDone]);

  return (
    <div className="bg-[var(--oj-cream)] text-[var(--oj-charcoal)]" data-testid="home-page">
      {!introDone && <BrandIntro onDone={() => setIntroDone(true)} />}
      <Header />
      <Hero />
      <Pillars />
      <About />
      <Signature />
      <TherapyGrid />
      <Programs />
      <Testimonials />
      <Visit />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default Home;
